import { CheckOutlined, RightOutlined } from '@ant-design/icons';
import { css, cx, keyframes } from '@emotion/css';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { Button, Checkbox, theme } from 'antd';
import type React from 'react';
import { memo, useEffect, useMemo, useState } from 'react';
import { HotkeyBadge } from '../../../components/HotkeyBadge.tsx';
import type {
  ParameterSelection_ProfileFragment,
  ParameterSelection_RequestableParameterFragment,
  ParameterToAddInput,
} from '../../../graphql/generated/graphql.ts';
import { ProfileDetails } from './ProfileDetails.tsx';

// Details are mounted on demand (only one profile is open at a time), so the open/close
// animations run as keyframes — animating grid-template-rows gets a real height transition
// without keeping every profile's parameter list in the DOM. Closing keeps the details
// mounted for the animation's duration, then unmounts.
//
// Height and opacity move together, mirroring antd's Collapse motion
// (antd/es/style/motion/collapse.js: motionDurationMid + motionEaseInOut for both
// directions) so the profile cards and the sheets accordion feel identical.
const revealAnimation = keyframes`
  from { grid-template-rows: 0fr; opacity: 0; }
  to { grid-template-rows: 1fr; opacity: 1; }
`;

const collapseAnimation = keyframes`
  from { grid-template-rows: 1fr; opacity: 1; }
  to { grid-template-rows: 0fr; opacity: 0; }
`;

const ProfileCard: React.FC<{
  profile: ParameterSelection_ProfileFragment;
  requestableByMainId: Map<string, ParameterSelection_RequestableParameterFragment>;
  selectedIds: Set<string>;
  disabledIds: Set<string>;
  showLab: boolean;
  expanded: boolean;
  selected: boolean;
  flipParamNames: boolean;
  highlight: string;
  onToggleExpand: (id: string) => void;
  onAddProfile: (profile: ParameterSelection_ProfileFragment) => void;
  onDeselectProfile: (profile: ParameterSelection_ProfileFragment) => void;
  onAddParam: (paramInput: ParameterToAddInput) => void;
  onRemove: (paramId: string) => void;
  onInfo: (param: ParameterSelection_RequestableParameterFragment) => void;
}> = ({
  profile,
  requestableByMainId,
  selectedIds,
  disabledIds,
  showLab,
  expanded,
  selected,
  flipParamNames,
  highlight,
  onToggleExpand,
  onAddProfile,
  onDeselectProfile,
  onAddParam,
  onRemove,
  onInfo,
}) => {
  const { token } = theme.useToken();

  // Unmount delay comes from the same token that drives the CSS, so the two can't drift.
  const revealMs = Math.round(Number.parseFloat(token.motionDurationMid) * 1000);

  // Keep the details mounted for the duration of the close animation, then drop them again.
  const [detailsMounted, setDetailsMounted] = useState(expanded);
  useEffect(() => {
    if (expanded) {
      setDetailsMounted(true);
      return;
    }
    const timeout = setTimeout(() => setDetailsMounted(false), revealMs);
    return () => clearTimeout(timeout);
  }, [expanded, revealMs]);

  const { known, selCount, selectedRequestableIds } = useMemo(() => {
    const selectedParamIds: string[] = [];
    for (const profileParam of profile.parameters) {
      const requestableParam = requestableByMainId.get(profileParam.parameter.id);
      if (requestableParam && selectedIds.has(requestableParam.id)) {
        selectedParamIds.push(requestableParam.id);
      }
    }
    return {
      known: profile.parameters.length,
      selCount: selectedParamIds.length,
      selectedRequestableIds: selectedParamIds,
    };
  }, [requestableByMainId, profile.parameters, selectedIds]);

  // The checkbox reflects the user's own pick, never the coincidental coverage of a profile
  // by parameters selected elsewhere — that only moves the counter. Once picked, the profile
  // stays indeterminate as long as some of its parameters could not be selected.
  const kind: 'all' | 'some' | 'none' = !selected || selCount === 0 ? 'none' : selCount === known ? 'all' : 'some';

  const onToggleSelect = () => {
    if (kind === 'none') {
      onAddProfile(profile);
    } else {
      for (const id of selectedRequestableIds) {
        onRemove(id);
      }
      onDeselectProfile(profile);
    }
  };

  const styles = useMemo(
    () => ({
      card: css`
        flex: none;
        border-radius: ${token.borderRadiusLG}px;
        overflow: hidden;
        background: ${token.colorBgContainer};
        transition: border-color 0.15s;
      `,
      cardAll: css`
        border: 1px solid ${token.colorPrimary};
      `,
      cardIdle: css`
        border: 1px solid ${token.colorBorderSecondary};
      `,
      head: css`
        display: flex;
        align-items: center;
        gap: ${token.marginSM}px;
        padding: ${token.paddingXS}px ${token.paddingSM}px;
        cursor: pointer;
        transition: background-color 0.15s;

        /* the head is a selectable control item — antd's token for that (= colorFillTertiary),
           deliberately a step above the 2% ProfileDetails body so the control sits over the content */
        &:hover {
          background: ${token.controlItemBgHover};
        }
      `,
      title: css`
        flex: 1;
        min-width: 0;
        display: flex;
        align-items: center;
        gap: ${token.marginXS}px;
      `,
      name: css`
        font-weight: 600;
        font-size: 14px;
        color: ${token.colorText};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      `,
      right: css`
        display: flex;
        align-items: center;
        gap: ${token.marginXS}px;
        flex: none;
      `,
      pillBase: css`
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 11.5px;
        font-weight: 600;
        padding: 3px 10px;
        border-radius: 999px;
        white-space: nowrap;
        line-height: 1.3;
        font-variant-numeric: tabular-nums;
      `,
      pillAll: css`
        background: color-mix(in srgb, ${token.colorPrimaryBg} 20%, ${token.colorBgContainer});
        color: ${token.colorPrimaryText};
        border: 1px solid ${token.colorPrimaryBorder};
      `,
      /* Coverage that is not a full, user-made selection is information only — kept a step
         above 'none' so profiles whose parameters are partly covered stay findable. */
      pillSome: css`
        background: transparent;
        color: ${token.colorTextSecondary};
        border: 1px solid ${token.colorBorderSecondary};
      `,
      pillNone: css`
        background: transparent;
        color: ${token.colorTextTertiary};
        border: 1px solid ${token.colorBorderSecondary};
      `,
      reveal: css`
        display: grid;
        grid-template-rows: 1fr;
        animation: ${revealAnimation} ${token.motionDurationMid} ${token.motionEaseInOut};
      `,
      revealClosing: css`
        display: grid;
        grid-template-rows: 0fr;
        animation: ${collapseAnimation} ${token.motionDurationMid} ${token.motionEaseInOut} forwards;
      `,
      revealInner: css`
        min-height: 0;
        overflow: hidden;
      `,
      chev: css`
        display: flex;
        transition: transform 0.18s;
      `,
      chevOpen: css`
        transform: rotate(90deg);
      `,
    }),
    [token]
  );

  const statusLabel = `${selCount} / ${known}`;
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div className={cx(styles.card, kind === 'none' ? styles.cardIdle : styles.cardAll)}>
      <div className={styles.head} onClick={() => onToggleSelect()}>
        <Checkbox checked={kind === 'all'} indeterminate={kind === 'some'} onClick={stop} onChange={() => onToggleSelect()} />
        <div className={styles.title}>
          <span className={styles.name}>
            <Markable tokens={highlight}>{profile.name}</Markable>
          </span>
          {profile.hotKey && <HotkeyBadge hotkey={profile.hotKey} />}
        </div>
        <div className={styles.right}>
          <span className={cx(styles.pillBase, kind === 'all' ? styles.pillAll : selCount > 0 ? styles.pillSome : styles.pillNone)}>
            {kind === 'all' && <CheckOutlined style={{ fontSize: 12 }} />}
            {statusLabel}
          </span>
          <Button
            size="small"
            onClick={e => {
              stop(e);
              onToggleExpand(profile.id);
            }}
            icon={
              <span className={cx(styles.chev, expanded && styles.chevOpen)}>
                <RightOutlined style={{ fontSize: 12 }} />
              </span>
            }
          />
        </div>
      </div>
      {detailsMounted && (
        <div className={expanded ? styles.reveal : styles.revealClosing}>
          <div className={styles.revealInner}>
            <ProfileDetails
              profile={profile}
              requestableByMainId={requestableByMainId}
              selectedIds={selectedIds}
              disabledIds={disabledIds}
              showLab={showLab}
              flipParamNames={flipParamNames}
              onAdd={onAddParam}
              onRemove={onRemove}
              onInfo={onInfo}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const MemoProfileCard = memo(ProfileCard);
